<template>
  <v-dialog :model-value="isVisible" width="700" persistent>
    <v-form>
      <v-card v-if="spot">
        <v-card-title class="d-flex flex-row pa-4 pb- ma-1">
          <h4 class="text-capitalize" data-testid="yard-asset-visit-detach-dialog-title">
            {{ t('yard.dialogs.yardViewAssetvisitDetachDialog.headers.dropAsset') }}
          </h4>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            density="comfortable"
            @click="$emit('close')"></v-btn>
        </v-card-title>
        <v-card-text>
          <p
            class="typography-body-md"
            v-html="
              t(
                'yard.dialogs.yardViewAssetvisitDetachDialog.messages.youCanOnlyDropOneAssetAtTheCurrentSpot'
              )
            "></p>

          <v-row class="mt-4">
            <v-col cols="6">
              <svg-asset-container class="fix-truck-rotation" :rotation="270" />

              <p class="mt-4 mb-6 typography-body-md-bold">
                {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.assetsBeingDropped') }}:
              </p>

              <div
                v-for="(assetContainer, i) in assetVisit.assetContainers"
                :key="assetContainer.id">
                <div>
                  <strong>{{ assetContainer.code }}</strong>
                </div>
                <v-chip size="x-small" variant="tonal" class="text-capitalize">
                  <strong>
                    {{ t(`core.yard.assetContainerTypes.${camelCase(assetContainer.type)}`) }}
                  </strong>
                </v-chip>

                <v-select
                  class="mt-2 pr-8"
                  v-if="assetVisit?.assetContainers?.length > 1"
                  v-model="assetContainerActions[assetContainer.id]"
                  :items="getAssetContainerActionItems(assetContainer.id)"
                  item-title="title"
                  item-value="value"
                  :item-props="
                    item => ({
                      title: item.title,
                      subtitle: item.subtitle,
                      disabled: item.disabled
                    })
                  "
                  density="compact"
                  color="primary"
                  variant="outlined"
                  hide-details></v-select>
                <v-spacer class="mt-8" />
              </div>
            </v-col>

            <v-divider vertical />

            <v-col cols="5" class="ml-4">
              <yard-view-truck
                :rotation="270"
                :asset-visit="assetVisit"
                :show-container-count="false"
                class="fix-truck-rotation" />

              <p class="mt-4 mb-6 typography-body-md-bold">
                {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.carrierDroppingOff') }}:
              </p>
              <h4 class="mt-3">
                <v-icon size="small" class="mr-1">mdi-truck-flatbed</v-icon>
                {{ assetVisit.company?.name || assetVisit.companyHint }}
              </h4>
              <p class="d-flex flex-column typography-body-sm" v-if="appointment?.refNumber">
                <strong>
                  {{
                    getTranslatedRefNumberDisplayName(
                      $refNumSettings(selectedWarehouse).displayName
                    )
                  }}
                </strong>
                {{ appointment.refNumber }}
              </p>

              <p class="mt-6">
                <strong>
                  {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.truckAssignment') }}:
                </strong>
              </p>
              <p class="mt-4">
                <v-radio-group v-model="assetVisitAction" density="compact" hide-details>
                  <v-radio
                    value="keep"
                    :label="
                      t('yard.dialogs.yardViewAssetvisitAttachDialog.labels.keepOnCurrentSpot')
                    " />
                  <v-radio
                    value="reassign"
                    :label="
                      t(
                        'yard.dialogs.yardViewAssetvisitAttachDialog.labels.reassignToDifferentSpot'
                      )
                    " />
                  <v-radio
                    value="depart"
                    :label="t('yard.dialogs.yardViewAssetvisitAttachDialog.labels.depart')" />
                </v-radio-group>
              </p>

              <p class="mt-4" v-if="assetVisitAction === 'reassign'">
                <strong>
                  {{ t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.assignTractorTo') }}:
                </strong>
                <yard-view-spot-select v-model="newSpotId" />
              </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="$emit('close')"
            data-testid="yard-asset-visit-detach-dialog-cancel">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            @click="submit"
            class="bg-primary"
            :disabled="!canConfirmDrop"
            data-testid="yard-asset-visit-detach-dialog-submit">
            {{ t('global.buttons.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { camelCase, capitalize } from 'lodash';
import { defineComponent, ref, watch, computed } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useEventHub } from '@/composables';
import { isUUID } from 'class-validator';
import { getTranslatedRefNumberDisplayName } from '@nova/core';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetVisitDetachDialog',
  methods: { camelCase },
  emits: ['close'],
  props: {
    spot: {
      type: [Object, null],
      required: true,
      default: null
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();
    const assetVisitAction = ref('keep');
    const assetContainerActions = ref({});
    const newSpotId = ref(null);
    const eventHub = useEventHub();
    const spotAssignment = computed(() => props.spot.spotAssignments[0]);
    const assetVisit = computed(() => spotAssignment.value?.assetVisit || {});
    const appointment = computed(() => assetVisit.value.appointment);

    const assetContainerActionItems = [
      {
        title: t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.dropAtSpot'),
        value: 'drop',
        subtitle: t(
          'yard.dialogs.yardViewAssetvisitAttachDialog.messages.detachTheAssetFromTheTruck'
        ),
        disabled: false
      },
      {
        title: t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.keepAttached'),
        value: 'keep',
        subtitle: t(
          'yard.dialogs.yardViewAssetvisitAttachDialog.messages.doNotDetachTheAssetFromTheTruck'
        )
      },
      {
        title: t('yard.dialogs.yardViewAssetvisitAttachDialog.messages.moveToWaitingList'),
        value: 'waiting',
        subtitle: t(
          'yard.dialogs.yardViewAssetvisitAttachDialog.messages.detachAndMoveThisAssetToTheWaitingAssignmentList'
        ),
        disabled: true
      }
    ];

    // We must do this in order to set the default values for the model,
    // As we can have multiple containers
    const setAssetContainerActionsValues = () => {
      if (assetVisit.value?.assetContainers?.[0]?.id) {
        assetContainerActions.value[assetVisit.value.assetContainers[0].id] = 'drop';
      }

      if (assetVisit.value?.assetContainers?.length > 1) {
        // set the rest of the containers to 'keep', excluding the first one
        assetVisit.value.assetContainers.slice(1).forEach(container => {
          assetContainerActions.value[container.id] = 'waiting';
        });
      }
    };

    const canConfirmDrop = computed(() => {
      if (assetVisitAction.value === 'reassign' && !isUUID(newSpotId.value)) {
        return false;
      }

      return true;
    });

    watch(
      () => props.isVisible,
      newVal => {
        assetContainerActions.value = {};

        if (newVal) {
          setAssetContainerActionsValues();
        }

        assetVisitAction.value = 'keep';
        newSpotId.value = null;
      }
    );

    const submit = async () => {
      const originalAssetVisit = { ...assetVisit.value };

      const res = await yardStore.detachAssetVisit(
        assetVisit.value.id,
        assetVisitAction.value,
        assetContainerActions.value,
        { spotAssignmentId: spotAssignment.value.id, newSpotId: newSpotId.value }
      );
      if (res) {
        if (assetVisitAction.value === 'reassign') {
          const newSpot = yardStore.spotsRef.value.find(spot => spot.id === newSpotId.value);
          const newSpotArea = yardStore.spotAreasRef.value.find(
            area => area.id === newSpot.spotAreaId
          );

          eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
            assetVisit: assetVisit.value,
            newSpotCode: newSpot.code,
            newSpotAreaName: newSpotArea.name
          });
        }

        Object.entries(assetContainerActions.value).forEach(([containerId, action]) => {
          if (action !== 'keep') {
            yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.DROP, {
              assetVisit: originalAssetVisit,
              metadata: {
                asset_container_id: containerId,
                asset_container_type: originalAssetVisit.assetContainers.find(
                  container => container.id === containerId
                ).type,
                asset_container_action: assetContainerActionItems.find(
                  item => item.value === action
                ).title,
                asset_visit_action: capitalize(assetVisitAction.value),
                appointment_id: originalAssetVisit.appointmentId
              }
            });
          }
        });

        emit('close');
      }
    };

    const getAssetContainerActionItems = assetContainerId => {
      const items = [...assetContainerActionItems];

      // If assetContainerActions.value has another drop at spot
      // we disable this one here, and enable the 'Move to Waiting list' option
      if (
        Object.entries(assetContainerActions.value).find(
          ([k, v]) => v === 'drop' && k !== assetContainerId
        )
      ) {
        items[0].disabled = true;
        items[2].disabled = false;
      } else {
        items[0].disabled = false;
        items[2].disabled = true;
      }

      return items;
    };

    return {
      t,
      getAssetContainerActionItems,
      newSpotId,
      assetContainerActions,
      assetVisitAction,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      assetVisit,
      appointment,
      canConfirmDrop,
      submit,
      getTranslatedRefNumberDisplayName
    };
  }
});
</script>
